import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Users, Trophy, Award, Copy, Check, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";

// Render a friendly relative-time string for the referral chain.
// We don't bring in date-fns just for this — three buckets is enough.
const fmtRelative = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diffMin = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
  if (diffMin < 60 * 24 * 30) return `${Math.round(diffMin / (60 * 24))}d ago`;
  return new Date(iso).toLocaleDateString();
};

// Dashboard "Referrals" card — surfaces the producer's profile-as-
// referral-link contribution so they can see their leaderboard
// score climb in near-real-time.
//
// Pulls from GET /api/leaderboard/me?period=30d which is a per-user
// focused count (no full-leaderboard aggregation, cheap).
//
// Auto-hides when the score is zero AND the user is new (created
// within 24h) — we don't want to greet brand-new users with "0
// referrals" on their first dashboard visit. After 24h we always
// show it because zero IS a meaningful state once you've been here
// long enough to share.
const ReferralStatsCard = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) return undefined;
    let stop = false;
    api.get(`/leaderboard/me?period=30d`)
      .then(r => { if (!stop) setStats(r.data); })
      .catch(() => { /* silent — card hides */ });
    return () => { stop = true; };
  }, [token]);

  if (!user || !stats) return null;
  // Note: we used to hide the card for brand-new (<24h) zero-score
  // users so their first dashboard wasn't a scoreboard of failures.
  // Dropped — a "0 referrals · share your link" state IS the prompt
  // we want new users to see. Card stays visible.

  const profileUrl = `${window.location.origin}/${user.username}`;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      toast.success(`Copied — GlobalEyes.TV/${user.username}`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy. Long-press the link to copy.");
    }
  };

  return (
    <Card className="bg-gradient-to-r from-gray-900 via-gray-900 to-amber-950/20 border-gray-800 mb-6" data-testid="referrals-card">
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-amber-300 text-xs sm:text-sm font-medium tracking-wide flex items-center gap-1.5">
            <Trophy className="h-3.5 w-3.5" /> YOUR REFERRALS · LAST 30 DAYS
          </p>
          {stats.rank ? (
            <Link
              to="/leaderboard"
              className="text-amber-400/80 hover:text-amber-300 text-[11px] font-semibold transition-colors"
              data-testid="referrals-rank-link"
            >
              Rank #{stats.rank} on the leaderboard →
            </Link>
          ) : (
            <Link
              to="/leaderboard"
              className="text-gray-500 hover:text-gray-300 text-[11px] transition-colors"
            >
              See leaderboard →
            </Link>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-3">
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-bold text-amber-300 tabular-nums" data-testid="referrals-profile-signups">
              {stats.profile_signups}
            </p>
            <p className="text-[10px] sm:text-[11px] text-gray-400 mt-0.5 flex items-center justify-center gap-1">
              <Users className="h-3 w-3" /> Profile signups
            </p>
            <p className="text-[10px] text-amber-400/70">×3 pts</p>
          </div>
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-bold text-emerald-300 tabular-nums" data-testid="referrals-patron-count">
              {stats.patron_referrals}
            </p>
            <p className="text-[10px] sm:text-[11px] text-gray-400 mt-0.5 flex items-center justify-center gap-1">
              <Award className="h-3 w-3" /> Patron conversions
            </p>
            <p className="text-[10px] text-emerald-400/70">×10 pts</p>
          </div>
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-bold text-white tabular-nums" data-testid="referrals-total-score">
              {stats.score}
            </p>
            <p className="text-[10px] sm:text-[11px] text-gray-400 mt-0.5">Total score</p>
            <p className="text-[10px] text-gray-500">{stats.waitlist_referrals} waitlist · ×1</p>
          </div>
        </div>

        {/* Referral chain — last 5 people who joined via this user's
            profile URL. Turns the abstract score into faces. Auto-hides
            when empty so brand-new accounts don't see a sad gap here. */}
        {Array.isArray(stats.recent_referrals) && stats.recent_referrals.length > 0 && (
          <div className="border-t border-gray-800/70 pt-2.5 mb-3" data-testid="referrals-chain">
            <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 font-semibold mb-2">
              Most recent joins
            </p>
            <ul className="flex flex-wrap gap-x-3 gap-y-1.5">
              {stats.recent_referrals.map((r) => (
                <li key={r.username} className="flex items-center gap-1.5 text-[11px]" data-testid={`referrals-chain-item-${r.username}`}>
                  {r.profile_image ? (
                    <img
                      src={r.profile_image}
                      alt={r.username}
                      className="h-5 w-5 rounded-full object-cover ring-1 ring-emerald-500/30"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <UserCircle2 className="h-5 w-5 text-gray-600" />
                  )}
                  <Link
                    to={`/${r.username}`}
                    className="font-mono text-emerald-300 hover:text-emerald-200 transition-colors"
                  >
                    @{r.username}
                  </Link>
                  <span className="text-gray-500">· {fmtRelative(r.created_at)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="border-t border-gray-800/70 pt-3 flex flex-col sm:flex-row sm:items-center gap-2">
          <p className="text-[11px] text-gray-400">
            Every signup at <span className="font-mono text-emerald-300">GlobalEyes.TV/{user.username}</span> counts. Share it.
          </p>
          <button
            onClick={handleCopy}
            className="sm:ml-auto inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 text-xs font-semibold hover:bg-emerald-500/20 transition-colors"
            data-testid="referrals-copy-link-btn"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralStatsCard;
